import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { map, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs'
import { ComissaoJulgadoraService } from '../comissao-julgadora-service'
import { ExaminadoresFiltro } from '../listagem-examinadores-filtro/listagem-examinadores-filtro.component'
import { Concurso } from 'src/app/models/concurso'
import { MatTableDataSource } from '@angular/material/table'
import { MatPaginator, PageEvent } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { ListagemExaminador } from 'src/app/models/listagem-examinador'
import { Titulacao } from 'src/app/models/examinador-unicamp-consulta'
import { UnespCoreAuthService, UnespCoreMessageService } from 'src/libs/unesp-core'
import { SelectionModel } from '@angular/cdk/collections'
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog'
import { TransferirPresidenciaDialogComponent } from '../transferir-presidencia-dialog/transferir-presidencia-dialog.component'
import { SubstituirSuplenteDialogComponent } from '../substituir-suplente-dialog/substituir-suplente-dialog.component'

@Component({
  selector: 'inscricoes-listagem-examinadores',
  templateUrl: './listagem-examinadores.component.html',
  styleUrls: ['./listagem-examinadores.component.scss'],
})
export class ListagemExaminadoresComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) public paginator!: MatPaginator
  @ViewChild(MatSort) public sort!: MatSort

  public currentFilters: ExaminadoresFiltro = { concurso: 0, papel: '', nome: '' }
  public filteredData: Concurso[] = []

  private destroy$ = new Subject<void>()

  public totalElements: number = 0
  sortField = 'concursoName'
  sortDirection = 'asc'

  public colunasGrid: string[] = [
    'select',
    'nome',
    'email',
    'matricula',
    'unidade',
    'titulacao',
    'titulacaoData',
    'papel',
    'tipo',
    'concursoName',
    'isNotificado',
    'isDeAcordo',
    'action',
  ]
  public dataSource: MatTableDataSource<ListagemExaminador> = new MatTableDataSource()

  selection = new SelectionModel<ListagemExaminador>(true, [])

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dataService: ComissaoJulgadoraService,
    private readonly unespCoreMessageService: UnespCoreMessageService,
    private readonly unespCoreAuthService: UnespCoreAuthService,
    public readonly dialog: MatDialog
  ) {}

  public ngOnInit(): void {
    this.paginator.pageSize = 20

    const parentId = this.route.parent?.snapshot?.params['id'] || this.route.parent?.parent?.snapshot?.params['id']
    const source$ = parentId
      ? (this.route.parent?.snapshot?.params['id'] ? this.route.parent!.params : this.route.parent!.parent!.params)
      : this.route.queryParams
    source$.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.currentFilters = {
        concurso: +(params['id'] || params['concurso']) || 0,
        nome: params['nome'] || '',
        papel: params['papel'] || '',
      }

      this.fetchData()
    })
  }

  public ngAfterViewInit(): void {
    this.sort?.sortChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.sortField = this.sort.active
      this.sortDirection = this.sort.direction || 'asc'
      this.paginator.pageIndex = 0
      this.fetchData()
    })
  }

  public handleFilterChange(newFilters: ExaminadoresFiltro): void {
    this.router.navigate([], {
      queryParams: newFilters,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    })
  }

  private fetchData(): void {
    this.fetchDataAsync().subscribe()
  }

  canModify(): boolean {
    return this.unespCoreAuthService.hasPermissionBasedRoles(['RH', 'DTA'])
  }

  canOpenOptions(): boolean {
    return this.unespCoreAuthService.hasPermissionBasedRoles(['RH', 'DTA', 'FINANCAS'])
  }

  getExaminadoresSuplentesFromConcurso(concursoName: string): ListagemExaminador[] {
    let a = this.dataSource.data.filter(
      examinador => examinador.concursoName === concursoName && examinador.papel === 'SUPLENTE'
    )
    return a
  }

  handleOpenSubstitution(comissaoJulgadoraRow: ListagemExaminador): void {
    const dialogRef = this.dialog.open(SubstituirSuplenteDialogComponent, {
      width: '400px',
      data: {
        comissaoJulgadoraId: comissaoJulgadoraRow.id,
        suplentes: this.getExaminadoresSuplentesFromConcurso(comissaoJulgadoraRow.concursoName),
      },
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.fetchData()
      }
    })
  }

  hasPresidenteInConcurso(concursoName: string): boolean {
    return this.dataSource.data.some(
      examinador => examinador.concursoName === concursoName && examinador.papel === 'PRESIDENTE'
    )
  }

  hasPresidente(): boolean {
    return this.dataSource.data.some(examinador => examinador.papel === 'PRESIDENTE')
  }

  definirPresidente(comissaoJulgadoraRow: ListagemExaminador): void {
    this.dataService.definirPresidente(comissaoJulgadoraRow.id)
      .pipe(
        switchMap(() => this.fetchDataAsync()),
        tap(() => this.unespCoreMessageService.showMessageSuccess('Presidente definido com sucesso!'))
      )
      .subscribe()
  }

  handleOpenTransferirPresidencia(comissaoJulgadoraRow: ListagemExaminador): void {
    const dialogRef = this.dialog.open(TransferirPresidenciaDialogComponent, {
      width: '400px',
      data: {
        comissaoJulgadoraId: comissaoJulgadoraRow.id,
      },
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.fetchData()
      }
    })
  }

  removerComissaoJulgadora(comissaoJulgadoraRow: ListagemExaminador): void {
    this.dataService.removerComissaoJulgadora(comissaoJulgadoraRow.id)
      .pipe(
        switchMap(() => this.fetchDataAsync()),
        tap(() => this.unespCoreMessageService.showMessageSuccess('Examinador removido com sucesso da comissão julgadora!'))
      )
      .subscribe()
  }

  private fetchDataAsync(): Observable<void> {
    const { concurso, papel, nome } = this.currentFilters
    const { pageIndex, pageSize } = this.paginator
    this.selection.clear()

    return this.dataService
      .listarExaminadores({
        search: nome,
        page: pageIndex,
        size: pageSize,
        papel: papel,
        concurso: concurso,
        sortField: this.sortField,
        sortDirection: this.sortDirection,
      })
      .pipe(
        map(data => {
          const { content, totalElements } = data

          this.dataSource = new MatTableDataSource(content)
          this.totalElements = totalElements
        })
      )
  }

  private readonly papelLabels: Record<string, string> = { TITULAR: 'Titular', SUPLENTE: 'Suplente', PRESIDENTE: 'Presidente', DESLIGADO: 'Desligado' }
  private readonly tipoLabels: Record<string, string> = { INTERNO: 'Interno', EXTERNO: 'Externo' }

  getPapel(papel: string): string { return this.papelLabels[papel] || papel }
  getTipo(tipo: string): string { return this.tipoLabels[tipo] || tipo }

  getTitulacao(titulacao: Titulacao) {
    const labels: Record<number, string> = { 1: 'Graduação', 2: 'Mestrado', 3: 'Doutorado', 4: 'Pós-Doutorado', 5: 'Livre-Docência', 6: 'Adjunto', 7: 'Titular' }; return labels[titulacao] || Titulacao[titulacao]
  }

  handlePageEvent($event: PageEvent) {
    this.fetchData()
  }

  public ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  notificarExaminador(comissaoJulgadora: ListagemExaminador) {
    const comissaoJulgadoraId = comissaoJulgadora.id

    this.dataService
      .notificarExaminador({ comissaoJulgadoraId })
      .pipe(
        switchMap(() => this.fetchDataAsync()),
        tap(() => this.unespCoreMessageService.showMessageSuccess('Examinador notificado com sucesso!'))
      )
      .subscribe()
  }

  notificarExaminadores(comissaoJulgadoraList: ListagemExaminador[]) {
    const comissaoJulgadoraIds = comissaoJulgadoraList.map(({ id }) => id)

    this.dataService
      .notificarExaminadores({ comissaoJulgadoraIds })
      .pipe(
        switchMap(() => this.fetchDataAsync()),
        tap(() => this.unespCoreMessageService.showMessageSuccess('Examinadores notificados com sucesso!'))
      )
      .subscribe()
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length
    const numRows = this.dataSource.data.length
    return numSelected === numRows
  }

  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear()
      return
    }

    this.selection.select(...this.dataSource.data)
  }

  checkboxLabel(row?: ListagemExaminador): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id + 1}`
  }

  openDialog(): void {
    const dialogRef = this.dialog.open(NotificarDialogComponent, {
      data: this.selection.selected,
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) this.notificarExaminadores(this.selection.selected)
    })
  }
}

@Component({
  selector: 'inscricoes-notificar-dialog',
  templateUrl: 'notificar-dialog.html',
})
export class NotificarDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<NotificarDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ListagemExaminador[]
  ) {}

  hasExaminadoresNotificados(): boolean {
    return this.data.some(examinador => examinador.isNotificado)
  }

  onNoClick(): void {
    this.dialogRef.close()
  }
}
